import { convertToImperial } from '../../../reducers/helpers'
import type { PlayerProfileResponse } from '../../../services/player/playerProfile'
import { resolveAssetUrl } from '../../../utils/assetUrl'
import { GENDER_UNSPECIFIED, initialPlayerValues } from './constants'
import type { EditPlayerFormValues, PreferredContactMethod } from './types'

const str = (v: unknown): string =>
  v === null || v === undefined ? '' : String(v)

/**
 * Normalize a BE date to the `YYYY-MM-DD` the DateOfBirthInput expects. The BE
 * may send a full ISO datetime (e.g. "1999-11-11T00:00:00+00:00"); the date
 * portion is the leading 10 chars. A plain "YYYY-MM-DD" passes through unchanged.
 *
 * Also the roster grids' DOB column, so both read a birth date the same way.
 */
export const toDateOnly = (v: unknown): string => {
  const s = str(v)
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : s
}

/**
 * Map a player profile API response to Edit Player form values. Height (metres)
 * is split into imperial ft/in for the UI; nulls become empty strings; the image
 * url is seeded from the thumbnail/full_path for the avatar display.
 */
export const mapPlayerToForm = (
  data: PlayerProfileResponse
): EditPlayerFormValues => {
  let heightFt = ''
  let heightIn = ''
  if (data.height !== null && data.height !== undefined && data.height !== '') {
    const metres =
      typeof data.height === 'string' ? parseFloat(data.height) : data.height
    if (!Number.isNaN(metres)) {
      const [ft, inches] = convertToImperial(metres)
      heightFt = str(ft)
      heightIn = str(inches)
    }
  }

  // Resolve through the asset resolver so the avatar src points at the active
  // CDN base (rewrites known S3 hosts). Falls back to filename when there's no
  // full_path, then '' rather than a broken src.
  const imageUrl = resolveAssetUrl(data.image) || data.image?.filename || ''

  const preferred =
    data.preferred_contact_method === 'phone' ||
    data.preferred_contact_method === 'email'
      ? (data.preferred_contact_method as PreferredContactMethod)
      : ''

  return {
    ...initialPlayerValues,
    firstName: str(data.name_first),
    lastName: str(data.name_last),
    birthDate: toDateOnly(data.birth_date),
    // No gender set on the BE → default the dropdown to "Unspecified" (its
    // sentinel), so an untouched form matches initial and omits gender_id.
    genderId:
      data.gender_id === null || data.gender_id === undefined
        ? GENDER_UNSPECIFIED
        : str(data.gender_id),
    heightFt,
    heightIn,
    weight: str(data.weight),
    imageUrl: imageUrl || '',
    imageData: '',
    imageDeleted: false,
    phoneNumber: str(data.phone_number),
    alternatePhoneNumber: str(data.alternate_phone_number),
    email: str(data.email),
    preferredContactMethod: preferred,
    preferredLanguage: str(data.preferred_language),
    emergencyContactName: str(data.emergency_contact_name),
    emergencyContactRelationship: str(data.emergency_contact_relationship),
    emergencyContactPhone: str(data.emergency_contact_phone_number),
    addressStreet1: str(data.address?.street_1),
    addressStreet2: str(data.address?.street_2),
    addressCity: str(data.address?.city),
    addressPostalCode: str(data.address?.postal_code),
    addressCountryId: str(data.address?.country?.id),
    addressProvinceId: str(data.address?.province?.id),
    instagram: str(data.instagram),
    facebook: str(data.facebook),
    tiktok: str(data.tiktok),
    youtube: str(data.youtube),
    leagueRegistrationNumber: str(data.league_registration_number),
    hockeyUsaNumber: str(data.hockey_usa_number),
    hockeyCanadaNumber: str(data.hockey_canada_number),
    customMembershipNumber: str(data.custom_membership_number),
    loyaltyNumber: str(data.loyalty_number),
  }
}
